<script lang="ts">
  import { get } from 'svelte/store'
  import { onMount } from 'svelte'
  import MainEditor from './MainEditor.svelte'
  import FileExplorer from './FileExplorer.svelte'
  import TabBar from './TabBar.svelte'
  import SaveIndicator from './SaveIndicator.svelte'
  import { fileStore, activeTab, hasUnsavedChanges, documents } from '$lib/stores/fileStore'
  import { FileText, FolderOpen, X, Menu } from 'lucide-svelte'
  import { useCustomEvent } from '$lib/hooks/useEventListener'
  
  interface Props {
    onOpenSettings?: () => void
  }
  
  let { onOpenSettings }: Props = $props()

  // Debug: Track mount/unmount
  console.log('[FileSystemWrapper] Component initialized')

  onMount(() => {
    console.log('[FileSystemWrapper] onMount called')
    console.trace('[FileSystemWrapper] onMount stack trace')
  })

  // File system state
  let showExplorer = $state(true)
  let currentTab = $state(get(activeTab))
  let saveStatus = $state<'saved' | 'saving' | 'error'>('saved')
  let lastSaved = $state<Date | null>(null)
  let fileExplorerRef = $state<FileExplorer | null>(null)

  // Debug: Track showExplorer changes
  $effect(() => {
    console.log('[FileSystemWrapper] showExplorer changed:', showExplorer)
  })
  
  // Subscribe to file store changes
  $effect(() => {
    const unsubscribe = activeTab.subscribe(tab => {
      currentTab = tab
    })
    
    return unsubscribe
  })
  
  // Toggle file explorer
  function toggleExplorer() {
    showExplorer = !showExplorer
  }
  
  // Create new document
  async function createNewDocument() {
    // Find an available untitled name
    const existingTitles = get(documents).map(doc => doc.title.toLowerCase())
    let title = 'untitled'
    let counter = 2
    
    while (existingTitles.includes(title.toLowerCase())) {
      title = `untitled ${counter}`
      counter++
    }
    
    await fileStore.createNewDocument(title)
  }
  
  // Handle focus explorer shortcut
  useCustomEvent('shortcut:focus-explorer', () => {
    console.log('shortcut:focus-explorer received in FileSystemWrapper')
    // Make sure explorer is visible
    if (!showExplorer) {
      showExplorer = true
    }
    // Use the component method if available, otherwise fallback to DOM
    setTimeout(async () => {
      console.log('Attempting to focus FileExplorer, ref:', !!fileExplorerRef)
      if (fileExplorerRef) {
        await fileExplorerRef.initializeFocus()
      } else {
        const fileExplorer = document.querySelector('.file-explorer') as HTMLElement
        console.log('Fallback to DOM, element found:', !!fileExplorer)
        if (fileExplorer) {
          fileExplorer.focus()
        }
      }
    }, 100) // Use setTimeout instead of requestAnimationFrame
  })
</script>

<div class="file-system-wrapper">
  <!-- Top Tab Bar -->
  <div class="top-bar">
    <button 
      class="explorer-toggle"
      onclick={toggleExplorer}
      title={showExplorer ? "Hide Files" : "Show Files"}
    >
      {#if showExplorer}
        <FolderOpen size={16} />
      {:else}
        <Menu size={16} />
      {/if}
    </button>
    
    <div class="tab-bar-container">
      <TabBar onNewDocument={createNewDocument} />
    </div>
    
    {#if currentTab}
      <div class="save-indicator-container">
        <SaveIndicator
          status={saveStatus}
          lastSaved={currentTab?.lastSaved || lastSaved}
          isDirty={currentTab?.isDirty}
        />
      </div>
    {/if}
  </div>
  
  <!-- Main Content Area -->
  <div class="content-area">
    <!-- File Explorer Sidebar -->
    {#if showExplorer}
      <aside class="file-explorer-sidebar">
        <FileExplorer bind:this={fileExplorerRef} />
      </aside>
    {/if}
    
    <!-- Editor Container -->
    <div 
      class="editor-container" 
      role="presentation"
      onclick={() => {
        // Dispatch custom event to clear file explorer focus
        window.dispatchEvent(new CustomEvent('clear-explorer-focus'))
      }}
      onkeydown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          window.dispatchEvent(new CustomEvent('clear-explorer-focus'))
        }
      }}>
      <MainEditor {onOpenSettings} />
    </div>
  </div>
</div>

<style>
  .file-system-wrapper {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: var(--bg-primary);
  }
  
  /* Top Bar with Tabs */
  .top-bar {
    display: flex;
    align-items: center;
    height: 36px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-primary);
    padding: 0 8px;
    gap: 8px;
  }
  
  .explorer-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .explorer-toggle:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
  
  .tab-bar-container {
    flex: 1;
    min-width: 0;
  }
  
  .save-indicator-container {
    display: flex;
    align-items: center;
    padding: 0 8px;
  }
  
  /* Content Area */
  .content-area {
    flex: 1;
    display: flex;
    overflow: hidden;
    position: relative;
  }
  
  /* File Explorer Sidebar */
  .file-explorer-sidebar {
    width: 240px;
    background: var(--bg-secondary);
    border-right: 1px solid var(--border-primary);
    overflow-y: auto;
    transition: width 0.3s ease;
  }
  
  /* Smooth transition when hiding */
  .file-explorer-sidebar:not(:global(.visible)) {
    width: 0;
    overflow: hidden;
  }
  
  /* Editor Container */
  .editor-container {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    position: relative;
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    .file-explorer-sidebar {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      z-index: 100;
      box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
    }
    
    .file-explorer-sidebar:not(:global(.visible)) {
      transform: translateX(-100%);
    }
  }
</style>